import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { formatPlaybackRate, formatPlaybackRuntime } from './format'
import { isSyncedTimelineEvent } from './resolveSession'

export type StoryScrubMark = {
  id: string
  /** Offset from story start, ms. */
  atMs: number
  title?: string
}

type StoryScrubberProps = {
  /** Story span for the bar (content / planned end). */
  durationMs: number
  /** Current playhead offset from story start. */
  elapsedMs: number
  /**
   * When set (e.g. Instant Replay pause point / tape-review stop), scrub clamps
   * here and the remainder of the full bar is blacked out — context without
   * shortening the window.
   */
  availableUntilMs?: number | null
  /**
   * Short name for the freeze mark + hover tip (default “Pause point”).
   * Tape review uses “Stop point”.
   */
  availableUntilLabel?: string | null
  marks?: StoryScrubMark[]
  disabled?: boolean
  /** Full-width bottom time bar (default). */
  variant?: 'player'
  ariaLabel?: string
  /** Hover / scrub tip label for a story offset (e.g. wall clock). */
  formatHoverMs?: (ms: number) => string
  /** Live preview while dragging (local only). */
  onScrubPreview?: (ms: number) => void
  /** Commit seek on release / tick click. */
  onScrubCommit: (ms: number) => void
  /** Pause-on-drag before the first move (recommended). */
  onScrubBegin?: () => void
}

/**
 * Shared story scrubber — custom track so the needle centers on 0% and 100%.
 * Interior beat marks only (not start/end ticks). Pause-on-drag; commit on release.
 */
export function StoryScrubber({
  durationMs,
  elapsedMs,
  availableUntilMs = null,
  availableUntilLabel = null,
  marks = [],
  disabled = false,
  variant = 'player',
  ariaLabel = 'Story position',
  formatHoverMs,
  onScrubPreview,
  onScrubCommit,
  onScrubBegin,
}: StoryScrubberProps) {
  const span = Math.max(1, durationMs)
  const scrubMax =
    availableUntilMs != null && Number.isFinite(availableUntilMs)
      ? Math.min(span, Math.max(0, availableUntilMs))
      : span
  const freezeLabel = (availableUntilLabel ?? 'Pause point').trim() || 'Pause point'
  const clamping = (ms: number) => Math.min(scrubMax, Math.max(0, ms))
  const value = clamping(elapsedMs)
  const blackoutPct =
    scrubMax < span - 0.5 ? Math.round(((span - scrubMax) / span) * 1000) / 10 : 0
  const freezePct = scrubMax < span - 0.5 ? Math.round((scrubMax / span) * 1000) / 10 : null
  const trackRef = useRef<HTMLDivElement | null>(null)
  const liveMsRef = useRef(value)
  const draggingRef = useRef(false)
  const beganRef = useRef(false)
  const [dragMs, setDragMs] = useState<number | null>(null)
  const [tip, setTip] = useState<{ text: string; left: number; top: number } | null>(null)
  /** Suppress tick jump-to-beat when the gesture was a scrub drag that started on a tick. */
  const tickDragMovedRef = useRef(false)

  if (!draggingRef.current) liveMsRef.current = value
  const shown = dragMs != null ? dragMs : value
  /* Quantize so 100ms server clock ticks don’t inchworm the thumb by sub-pixels. */
  const pct = Math.round((shown / span) * 1000) / 10

  // Include story-start / story-end beats so first & last cards get ticks + tips.
  const interiorMarks = marks.filter((m) => m.atMs >= 0 && m.atMs <= span)

  function msAlongTrack(clientX: number): number {
    const el = trackRef.current
    if (!el) return liveMsRef.current
    const rect = el.getBoundingClientRect()
    const w = Math.max(1, rect.width)
    const t = Math.min(1, Math.max(0, (clientX - rect.left) / w))
    return Math.round(t * span)
  }

  /** Scrub head — clamped to the recorded window when IR blackout is active. */
  function msFromClientX(clientX: number): number {
    return clamping(msAlongTrack(clientX))
  }

  function placeTip(text: string, clientX: number, clientY: number): void {
    // Keep tips on-screen at the 0% / 100% ends (translateX(-50%) would clip).
    const approxHalf = Math.min(140, text.length * 3.5 + 16)
    const pad = 10
    const left = Math.max(
      pad + approxHalf,
      Math.min(window.innerWidth - pad - approxHalf, clientX)
    )
    const top = Math.max(pad + 28, clientY - 10)
    setTip({ text, left, top })
  }

  function timeLabel(ms: number): string {
    const fallback = formatPlaybackRuntime(ms).replace(/^0:/, '') || formatPlaybackRuntime(ms)
    return formatHoverMs?.(ms) ?? fallback
  }

  /** Nearest beat within a thin hover band — append title; never replace the timestamp. */
  function beatNearMs(ms: number): StoryScrubMark | null {
    const tol = Math.max(350, Math.round(span * 0.01))
    let best: StoryScrubMark | null = null
    let bestD = Infinity
    for (const m of interiorMarks) {
      const d = Math.abs(m.atMs - ms)
      if (d <= tol && d < bestD) {
        best = m
        bestD = d
      }
    }
    return best
  }

  function tipForMs(
    ms: number,
    clientX: number,
    clientY: number,
    forceMark?: StoryScrubMark | null,
  ): void {
    const time = timeLabel(ms)
    const beat = (forceMark ?? beatNearMs(ms))?.title?.trim()
    const nearFreeze =
      scrubMax < span - 0.5 && Math.abs(ms - scrubMax) <= Math.max(350, Math.round(span * 0.01))
    let text = beat ? `${time} · ${beat}` : time
    // No "Not yet recorded" — it fought story-tick hover tips in the blackout.
    if (nearFreeze) text = `${text} · ${freezeLabel}`
    placeTip(text, clientX, clientY)
  }

  function beginDrag(clientX: number, clientY: number): void {
    if (disabled) return
    draggingRef.current = true
    if (!beganRef.current) {
      beganRef.current = true
      onScrubBegin?.()
    }
    const ms = msFromClientX(clientX)
    liveMsRef.current = ms
    setDragMs(ms)
    tipForMs(ms, clientX, clientY)
    onScrubPreview?.(ms)
  }

  function moveDrag(clientX: number, clientY: number): void {
    if (!draggingRef.current || disabled) return
    tickDragMovedRef.current = true
    const ms = msFromClientX(clientX)
    liveMsRef.current = ms
    setDragMs(ms)
    tipForMs(ms, clientX, clientY)
    onScrubPreview?.(ms)
  }

  function endDrag(): void {
    if (!draggingRef.current) return
    draggingRef.current = false
    beganRef.current = false
    const ms = clamping(liveMsRef.current)
    setDragMs(null)
    setTip(null)
    onScrubCommit(ms)
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>): void {
    if (disabled) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    beginDrag(e.clientX, e.clientY)
  }

  return (
    <div
      className={`sc-story-scrub sc-story-scrub-${variant}${disabled ? ' is-disabled' : ''}`}
    >
      <div
        ref={trackRef}
        className="sc-story-scrub-track"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={scrubMax}
        aria-valuenow={shown}
        aria-disabled={disabled || undefined}
        onPointerDown={onPointerDown}
        onPointerMove={(e) => {
          if (draggingRef.current) {
            moveDrag(e.clientX, e.clientY)
            return
          }
          // Hover tips use the full story span (incl. blackout); scrub still clamps.
          tipForMs(msAlongTrack(e.clientX), e.clientX, e.clientY)
        }}
        onPointerLeave={() => {
          if (!draggingRef.current) setTip(null)
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        onKeyDown={(e) => {
          if (disabled) return
          const step = Math.max(1000, Math.round(span / 100))
          let next: number | null = null
          if (e.key === 'ArrowDown') next = clamping(shown - step)
          if (e.key === 'ArrowUp') next = clamping(shown + step)
          if (e.key === 'Home') next = 0
          if (e.key === 'End') next = scrubMax
          if (next == null) return
          e.preventDefault()
          onScrubBegin?.()
          onScrubPreview?.(next)
          onScrubCommit(next)
        }}
      >
        <div className="sc-story-scrub-rail" aria-hidden="true" />
        <div className="sc-story-scrub-fill" style={{ width: `${pct}%` }} aria-hidden="true" />
        {blackoutPct > 0 ? (
          <div
            className="sc-story-scrub-blackout"
            style={{ width: `${blackoutPct}%` }}
            aria-hidden="true"
          />
        ) : null}
        {freezePct != null ? (
          <div
            className="sc-story-scrub-freeze"
            style={{ left: `${freezePct}%` }}
            aria-hidden="true"
            title={`${freezeLabel} — Instant Replay ends here`}
            onMouseEnter={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
              tipForMs(scrubMax, rect.left + rect.width / 2, rect.top - 6)
            }}
            onMouseLeave={() => {
              if (!draggingRef.current) setTip(null)
            }}
          />
        ) : null}
        <div className="sc-story-scrub-thumb" style={{ left: `${pct}%` }} aria-hidden="true">
          <span className="sc-story-scrub-needle" />
        </div>
        <div className="sc-story-scrub-ticks">
          {interiorMarks.map((mark) => (
            <button
              key={mark.id}
              type="button"
              className={`sc-story-scrub-tick${disabled ? ' is-locked' : ''}`}
              tabIndex={-1}
              aria-disabled={disabled || undefined}
              aria-label={mark.title ? `Jump to ${mark.title}` : 'Jump to card'}
              style={{ left: `${(mark.atMs / span) * 100}%` }}
              onPointerDown={() => {
                // Do not stopPropagation — thumb-on-tick must still start a scrub drag.
                tickDragMovedRef.current = false
              }}
              onMouseEnter={(e) => {
                // Same YouTube tip: keep the time, append the beat name when on a tick.
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                tipForMs(mark.atMs, rect.left + rect.width / 2, rect.top - 6, mark)
              }}
              onMouseLeave={(e) => {
                if (draggingRef.current) return
                const track = trackRef.current
                if (track) {
                  const r = track.getBoundingClientRect()
                  const { clientX: x, clientY: y } = e
                  if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
                    tipForMs(msFromClientX(x), x, y)
                    return
                  }
                }
                setTip(null)
              }}
              onClick={(e) => {
                e.stopPropagation()
                if (disabled) return
                // Drag that began on this tick already committed via the track — skip jump.
                if (tickDragMovedRef.current) {
                  tickDragMovedRef.current = false
                  return
                }
                if (mark.atMs > scrubMax + 80) return
                beganRef.current = false
                draggingRef.current = false
                setDragMs(null)
                setTip(null)
                onScrubBegin?.()
                const at = clamping(mark.atMs)
                liveMsRef.current = at
                onScrubPreview?.(at)
                onScrubCommit(at)
              }}
            />
          ))}
        </div>
      </div>
      {tip && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="sc-story-scrub-tip is-portal"
              style={{ left: tip.left, top: tip.top }}
              role="tooltip"
            >
              {tip.text}
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

/**
 * Next / previous story-beat offset from marks.
 * ← : if the playhead is past a beat, land on that beat first; if already on/at a beat,
 *     step to the previous one. (Old `ms < elapsed - eps` skipped the beat you just
 *     passed and jumped to story start / no-op'd with a stale nav anchor.)
 */
export function adjacentBeatMs(
  marks: StoryScrubMark[],
  elapsedMs: number,
  direction: 1 | -1,
  durationMs: number
): number | null {
  const span = Math.max(1, durationMs)
  // Include 0 — first story beat is often at story start (was filtered out before).
  const sorted = [
    ...new Set(
      marks
        .map((m) => m.atMs)
        .filter((ms) => ms >= 0 && ms <= span)
        .map((ms) => Math.round(ms))
    ),
  ].sort((a, b) => a - b)
  if (!sorted.length) return null
  const eps = 80
  if (direction > 0) {
    for (const ms of sorted) {
      if (ms > elapsedMs + eps) return ms
    }
    return null
  }
  // Any progress past a beat → land on that beat first. Already on a beat → previous.
  // (Using a large eps here skipped the nearby mark and jumped to story start.)
  for (let i = sorted.length - 1; i >= 0; i--) {
    const ms = sorted[i]!
    if (elapsedMs > ms + 1) return ms
  }
  for (let i = sorted.length - 1; i >= 0; i--) {
    const ms = sorted[i]!
    if (ms < elapsedMs - eps) return ms
  }
  return null
}

type PlaybackSpeedMenuProps = {
  rate: number
  rates: number[]
  fullRuntimeMs: number
  disabled?: boolean
  /** Prefer menu above (bottom bar) or below (top strip). */
  menuPlacement?: 'up' | 'down'
  /** Overrides the default runtime tooltip (e.g. why the control is locked). */
  title?: string
  onPickRate: (rate: number) => void
}

/** YouTube-like speed menu — portaled so strip overflow cannot clip it. */
export function PlaybackSpeedMenu({
  rate,
  rates,
  fullRuntimeMs,
  disabled = false,
  menuPlacement = 'up',
  title,
  onPickRate,
}: PlaybackSpeedMenuProps) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    if (!open) return
    const place = () => {
      const btn = btnRef.current
      if (!btn) return
      const r = btn.getBoundingClientRect()
      const popH = popRef.current?.offsetHeight ?? 200
      const top =
        menuPlacement === 'down' ? r.bottom + 6 : Math.max(8, r.top - popH - 6)
      setPos({ top, left: r.left + r.width / 2 })
    }
    place()
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, menuPlacement])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  const label = formatPlaybackRate(rate)
  const menu =
    open && pos ? (
      <div
        ref={popRef}
        className="sc-speed-menu-pop sc-speed-menu-pop-portal"
        role="menu"
        aria-label="Playback speed"
        style={{ top: pos.top, left: pos.left }}
      >
        {rates.map((r) => {
          const runtime = Math.round(fullRuntimeMs / Math.max(PLAYBACK_RATE_SAFE, r))
          const active = Math.abs(r - rate) < 1e-6
          return (
            <button
              key={r}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              className={`sc-speed-menu-item${active ? ' is-on' : ''}`}
              onClick={() => {
                onPickRate(r)
                setOpen(false)
              }}
            >
              <span className="sc-speed-menu-rate">{formatPlaybackRate(r)}</span>
              <span className="sc-speed-menu-runtime">{formatPlaybackRuntime(runtime)}</span>
            </button>
          )
        })}
      </div>
    ) : null

  return (
    <div
      className={`sc-speed-menu${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
    >
      <button
        ref={btnRef}
        type="button"
        className="sc-btn sc-speed-menu-btn"
        disabled={disabled || rates.length <= 1}
        aria-haspopup="menu"
        aria-expanded={open}
        title={
          title ??
          `Playback speed · ${formatPlaybackRuntime(
            Math.round(fullRuntimeMs / Math.max(PLAYBACK_RATE_SAFE, rate))
          )} to watch`
        }
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  )
}

const PLAYBACK_RATE_SAFE = 0.01

/**
 * Interior beat marks from the display timeline (skip exact start/end).
 * Prefer authored story beats; fall back to sync rows when the board is sparse.
 */
export function scrubMarksFromTimeline(
  events: { id: string; at: string; title: string }[],
  durationMs: number,
  offsetMs: (iso: string) => number,
  maxMarks = 28
): StoryScrubMark[] {
  const span = Math.max(1, durationMs)
  const authored: StoryScrubMark[] = []
  const synced: StoryScrubMark[] = []
  for (const ev of events) {
    const atMs = offsetMs(ev.at)
    // Keep story-start beats (0) for ←/→ navigation; ticks still hide exact 0/end.
    // Allow atMs === span (last narrative card) so the end tick/tip exists.
    if (atMs < 0 || atMs > span) continue
    const mark = { id: ev.id, atMs, title: ev.title }
    if (isSyncedTimelineEvent(ev)) synced.push(mark)
    else authored.push(mark)
  }
  const raw = authored.length > 0 ? authored : synced
  if (raw.length <= maxMarks) return raw
  const step = raw.length / maxMarks
  const picked: StoryScrubMark[] = []
  for (let i = 0; i < maxMarks; i++) {
    picked.push(raw[Math.min(raw.length - 1, Math.floor(i * step))]!)
  }
  return picked
}
